'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function AuthPage() {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const { t } = useLanguage();

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative bg-primary flex-col justify-between p-12 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-white/5 translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-white/5 -translate-x-1/4 translate-y-1/4" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full bg-white/3 -translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={44} />
          <span className="font-display font-bold text-2xl text-white tracking-tight">ServeNest</span>
        </div>

        {/* Hero content */}
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white/90 text-xs font-medium px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            Trusted by 12,000+ customers
          </div>
          <h2 className="font-display text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            Premium Services,<br />
            <span className="text-accent">Delivered to You</span>
          </h2>
          <p className="text-white/75 text-lg leading-relaxed mb-8">
            Book verified maids, rent luxury apartments, and access exclusive hospitality services — all in one place.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
            { value: '240+', label: 'Verified Maids' },
            { value: '85', label: 'Premium Apartments' },
            { value: '4.8★', label: 'Average Rating' }]?.
            map((stat) =>
            <div key={`stat-${stat?.label}`} className="bg-white/10 rounded-xl p-4 text-center">
                <p className="font-display font-bold text-2xl text-white tabular-nums">{stat?.value}</p>
                <p className="text-white/65 text-xs mt-1">{stat?.label}</p>
              </div>
            )}
          </div>
        </div>

        {/* Testimonial */}
        <div className="relative bg-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1133e9c52-1772892740916.png"
              alt="Happy customer testimonial profile photo"
              className="w-10 h-10 rounded-full object-cover" />

            <div>
              <p className="font-display font-semibold text-white text-sm">Melissa Park</p>
              <p className="text-white/60 text-xs">Verified Customer</p>
            </div>
          </div>
          <p className="text-white/80 text-sm italic leading-relaxed">
            "ServeNest made finding a reliable maid and a great apartment incredibly easy. The quality of service is consistently excellent."
          </p>
        </div>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-10 bg-background overflow-y-auto">
        {/* Mobile logo + language switcher */}
        <div className="w-full max-w-md flex items-center justify-between mb-8 lg:mb-0">
          <div className="lg:hidden flex items-center gap-2">
            <AppLogo size={36} />
            <span className="font-display font-bold text-xl text-foreground">THC</span>
          </div>
          <div className="lg:hidden ml-auto">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Language switcher for desktop (top right of form area) */}
        <div className="hidden lg:flex w-full max-w-md justify-end mb-4">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-md">
          {/* Tabs */}
          <div className="flex items-center bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
              activeTab === 'login' ? 'bg-white text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'}`
              }>
              {t('auth.signIn')}
            </button>
            <button
              onClick={() => setActiveTab('signup')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
              activeTab === 'signup' ? 'bg-white text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'}`
              }>
              {t('auth.createAccount')}
            </button>
          </div>

          {/* Forms */}
          {activeTab === 'login' ?
          <LoginForm onSwitchToSignup={() => setActiveTab('signup')} /> :
          <SignupForm onSwitchToLogin={() => setActiveTab('login')} />
          }

          {/* Back to browsing */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('auth.justBrowsing')}{' '}
            <Link href="/services-booking" className="text-primary font-medium hover:underline">
              {t('auth.viewServices')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}